import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { XmlEditorConfig } from './code/xmlEditorConfig';
import { XMLCursorPositions } from './code/cursor/xmlCursorPos.class';
import { DomDummyNodeManager } from './code/dummyNodes/domDummyNodeManager.class';
import { Component, OnChanges, Input, ViewChild, Output, EventEmitter, Renderer2, forwardRef, ViewEncapsulation, OnDestroy, ElementRef } from '@angular/core';
import { AngularXmlEditorService } from './angular-xml-editor.service';
import { XmlEditorContext } from './code/xmlEditorContext.class';
import { Xml2html } from './code/xml-html-converter/xml2html.class';
import { Html2xml } from './code/xml-html-converter/html2xml.class';
import { DebugContext } from '@angular/core/src/view';

@Component({
  selector: 'axed-editor',
  templateUrl: './angular-xml-editor.component.html',
  styleUrls: ['./angular-xml-editor.component.scss'],
  encapsulation: ViewEncapsulation.None, // to prevent the scss to rename the class names and not match the new generated xml dom
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AngularXmlEditorComponent),
      multi: true
    }
  ]
})
export class AngularXmlEditorComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  private editorContext: XmlEditorContext;
  isDebugMode = false;

  @Input('config')
  get config(): XmlEditorConfig {
    if (this.editorContext !== undefined && this.editorContext.config !== undefined) {
      return this.editorContext && this.editorContext.config;
    }
    return undefined;
  }

  @Input('editorContextId')
  set editorContextId(value: string) {
    if (!value) {
      return;
    }
    this.editorContext = this.editorService.getEditorContext(value);
  }

  @Input('debugMode')
  set debugMode(value: boolean) {
    this.isDebugMode = value;
  }

  @ViewChild('editor')
  textArea: ElementRef;
  @ViewChild('editorWrapper')
  editorWrapper: ElementRef;

  @Output()
  public valueChange = new EventEmitter();

  // actualXmlCursor: XmlCursor = new XmlCursor();
  domDummyNodeManager = new DomDummyNodeManager();

  private onTouchedFunction: () => void;
  private onChangeFunction: (value: string) => void;

  constructor(private _renderer: Renderer2, private editorService: AngularXmlEditorService) {}

  ngOnChanges() {}

  ngOnDestroy(): void {}

  onLostFocus() {
    this.editorContext.saveSelection(); // save selection on lost focus
    if (typeof this.onTouchedFunction === 'function') {
      this.onTouchedFunction();
    }
    return;
  }

  onGotFocus() {
    this.editorContext.restoreSelection();
    this.textArea.nativeElement.focus(); // set focus to the text area
  }

  clicked() {}

  mousedown() {}

  mouseup(mouseEvent: MouseEvent) {
    return this.editorContext.mouseUp(mouseEvent);
  }

  pasted(text: string): boolean {
    return this.editorContext.pasted(text);
  }

  copied(): boolean {
    return this.editorContext.copied();
  }

  keyDown(event: KeyboardEvent): boolean {
    return this.editorContext.keyDown(event);
  }

  keyUp(event: KeyboardEvent): boolean {
    return this.editorContext.keyUp(event);
  }

  // exec(): boolean {
  //   let userSelection: Selection;
  //   if (window.getSelection) {
  //     userSelection = window.getSelection();
  //     this.editorBreadCrumb.showSelection(userSelection);
  //     this.actualXmlCursor.StartPos.SetCursor(userSelection.anchorNode, XMLCursorPositionen.CursorInsideTextNode, userSelection.anchorOffset);
  //     this.commander.setCursorPos(this.actualXmlCursor.StartPos);
  //   }

  //   this.domDummyNodeManager.UpdateDummyNodes(this.actualXmlCursor.xmlDocumentRootNode, false);

  //   return false;
  // }

  showXml(xml: string): void {
    if (this.textArea && this.editorContext && this.editorContext.config && this.editorContext.config.rules) {
      if (xml === undefined) {
        return;
      }

      const xml2html = new Xml2html(this.editorContext.config.rules);
      const html = xml2html.ToHtml(xml);
      const normalizedValue = xml === null ? '' : html;
      this._renderer.setProperty(this.textArea.nativeElement, 'innerHTML', normalizedValue);
    }
  }

  /* START OF ControlValueAccessor  (see https://angular.io/api/forms/ControlValueAccessor ) */

  /**
   * Sets the function called when the control gets a change event
   */
  registerOnChange(func: any): void {
    this.onChangeFunction = func;
  }

  /**
   * Sets the function called when the control gets a touch event
   */
  registerOnTouched(func: any): void {
    this.onTouchedFunction = func;
  }

  /**
   * Write the new value to the element
   */
  writeValue(value: any): void {
    if (this.editorContext && this.editorContext.config) {
      if (value === null || value === undefined || value === '') {
        value = undefined;
      }

      this.showXml(value);

      if (value === undefined) {
        this.editorContext.actualCursor.xmlDocumentRootNode = undefined;
      } else {
        const el = this.textArea.nativeElement as Element;
        const node = el.firstChild as Element;
        this.editorContext.actualCursor.xmlDocumentRootNode = node;
        this.editorContext.actualCursor.StartPos.SetCursor(this.editorContext.actualCursor.xmlDocumentRootNode, XMLCursorPositions.CursorOnCompleteNode);
        this.domDummyNodeManager.UpdateDummyNodes(this.editorContext.actualCursor.xmlDocumentRootNode);
      }
    }
  }

  /**
   * Executed from the contenteditable section while the input property changes
   * @param html html string from contenteditable
   */
  onContentChange(html: string): void {
    if (typeof this.onChangeFunction === 'function') {
      const html2xml = new Html2xml(this.editorContext.config.rules);
      const xml = html2xml.ToXml(html);
      const normalizedValue = xml === null ? '' : xml;
      this.onChangeFunction(normalizedValue);
    }
    return;
  }

  /**
   * sets the disabled state for this element
   */
  setDisabledState(isDisabled: boolean): void {
    const div = this.textArea.nativeElement;
    const action = isDisabled ? 'addClass' : 'removeClass';
    this._renderer[action](div, 'disabled');
  }

  /* END OF ControlValueAccessor */
}
